import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "kg_theme";

/**
 * Two themes: 'dark' (default, our editorial luxury palette) and 'light'
 * (day mode — bone background, ink text, brass accents preserved).
 * The theme is applied to <html> as a `data-theme` attribute so CSS
 * overrides in index.css can flip colours without touching any component.
 */
export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved === "light" || saved === "dark") return saved;
        } catch {}
        return "dark";
    });

    // Apply attribute to <html>
    useEffect(() => {
        try {
            document.documentElement.setAttribute("data-theme", theme);
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {}
    }, [theme]);

    const setTheme = useCallback((t) => setThemeState(t === "light" ? "light" : "dark"), []);
    const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggle, isLight: theme === "light" }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
}
